import { watch, Ref, ref, onMounted, onBeforeUnmount, nextTick, toRaw, computed, toRefs, onUpdated, shallowRef } from 'vue';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';

interface Props {
  options: EChartsOption;
  height?: number | string;
  width?: number | string;
  responsiveWidth?: boolean;
  responsiveHeight?: boolean;
  eventType?: string;
  function?: Function;
}

/** Strip alignTicks: true from radar config to prevent ECharts warning
 *  when indicator max values have a wide numeric range (e.g. 0–1,400,000).
 *  alignTicks causes ECharts to try aligning all radar spokes to the same
 *  tick interval, which fails when scales differ by orders of magnitude. */
function sanitizeRadarOptions(options: EChartsOption): EChartsOption {
  if (!options.radar) return options;
  const radar = options.radar as any;
  if (Array.isArray(radar)) {
    return { ...options, radar: radar.map((r: any) => ({ ...r, alignTicks: false })) };
  }
  return { ...options, radar: { ...radar, alignTicks: false } };
}

export default function (props: Props) {
  const chart = shallowRef();
  const id = ref();

  const uid = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  id.value = uid();

  const resolveHeight = computed(() => {
    return props.responsiveHeight ? '100%' : typeof props.height === 'number' ? `${props.height}px` : props.height;
  });

  const resolveWidth = computed(() => {
    return props.responsiveWidth ? '100%' : typeof props.width === 'number' ? `${props.width}px` : props.width;
  });

  // เพื่อนบ้าน (เช่น legend ที่ auto-scale font) ตั้งขนาดตัวเองเสร็จช้ากว่า reflow แรกของ browser (ใช้ Vue watch + nextTick)
  // ResizeObserver ของกราฟเองอาจจับขนาดที่ยังไม่นิ่งตอน resize จอ ทำให้ canvas ค้างขนาดผิดจนกว่าจะ resize อีกครั้ง
  // จึงฟัง window resize เพิ่มเป็น safety net แล้ว resize() ซ้ำหลัง debounce ให้ layout ข้างเคียงนิ่งก่อน
  let windowResizeTimeout: ReturnType<typeof setTimeout> | undefined;
  const handleWindowResize = () => {
    clearTimeout(windowResizeTimeout);
    windowResizeTimeout = setTimeout(() => {
      chart.value?.resize();
    }, 300);
  };

  const init = () => {
    const dom = document.getElementById(String(id.value)) as HTMLElement;
    try {
      chart.value = echarts.init(dom);
      chart.value.setOption(sanitizeRadarOptions(toRaw(props.options)));
      let initResize = false;

      setTimeout(() => {
        initResize = true;
        chart.value.resize();
      }, 1000);

      new ResizeObserver(() => {
        if (initResize) chart.value.resize();
      }).observe(dom);

      if (props.eventType) {
        chart.value.on(props.eventType, props.function);
      }
    } catch (e) {
      //  TODO: this exception will happen when using with vue <transition>
    }
  };

  onMounted(() => {
    init();
    window.addEventListener('resize', handleWindowResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleWindowResize);
    clearTimeout(windowResizeTimeout);
  });

  watch(
    () => props.options,
    () => {
      if (chart.value) chart.value.setOption(sanitizeRadarOptions(toRaw(props.options)));
    },
    {
      deep: true,
      immediate: true,
    },
  );

  watch([() => props.height, () => props.width], () => {
    setTimeout(() => {
      chart.value.resize();
    }, 100);
  });

  return { id, resolveHeight, resolveWidth };
}
